import { useEffect, useState, useMemo } from 'react';
import { schoolsApi, type School, type SchoolAdmission } from '../api';
import { useStudent } from '../context/StudentContext';
import { Search, MapPin, Filter, X, ChevronDown, Star, TrendingUp, Award, School as SchoolIcon, ExternalLink, ArrowLeft, SlidersHorizontal } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import AcademicianDetailPanel from '../components/AcademicianDetailPanel';

interface SchoolFilters {
  type: 'all' | '985' | '211' | 'dual';
  hasAcademician: boolean;
  city: string;
  province: string;
  search: string;
}

export default function SchoolExplorer() {
  const { currentStudent } = useStudent();
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<SchoolFilters>({ type: 'all', hasAcademician: false, city: '', province: '', search: '' });
  const [cities, setCities] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: keyof School; direction: 'asc' | 'desc' } | null>(null);
  // 统计数据（来自后端的真实统计）
  const [stats, setStats] = useState({ total: 0, is985: 0, is211: 0, dual: 0, has_academician: 0 });
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  
  // 详情弹窗
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [admissionData, setAdmissionData] = useState<SchoolAdmission[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // 移动端筛选面板展开
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // 院士视图（详情弹窗内翻转）
  const [detailView, setDetailView] = useState<'school' | 'academician'>('school');
  const [academicians, setAcademicians] = useState<any[]>([]);
  const [academiciansLoading, setAcademiciansLoading] = useState(false);

  // Safari 对 CSS 3D `rotateY(180deg)` + `backface-visibility: hidden` 有渲染 bug，
  // 会导致背面内容水平镜像。仅在非 Safari 浏览器启用 3D 翻转动画。
  const isSafari = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
  }, []);

  useEffect(() => {
    loadSchools();
    loadMeta();
    loadStats();
  }, []);

  const loadSchools = async () => {
    await loadSchoolsWithFilters(filters);
  };

  const loadSchoolsWithFilters = async (currentFilters: SchoolFilters, currentPage = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page: currentPage, pageSize };
      if (currentFilters.type === '985') params.is985 = 1;
      if (currentFilters.type === '211') params.is211 = 1;
      if (currentFilters.type === 'dual') params.is_dual_class = 1;
      if (currentFilters.hasAcademician) params.has_academician = 1;
      if (currentFilters.city) params.city = currentFilters.city;
      if (currentFilters.province) params.province = currentFilters.province;
      if (currentFilters.search) params.search = currentFilters.search;
      const res = await schoolsApi.list(params);
      setSchools(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(currentPage);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMeta = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        schoolsApi.provinces(),
        schoolsApi.cities()
      ]);
      setProvinces(pRes.data.data || []);
      setCities(cRes.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // 加载统计数据（支持省市筛选）
  const loadStats = async (province?: string, city?: string) => {
    try {
      const params: Record<string, string> = {};
      if (province) params.province = province;
      if (city) params.city = city;
      const sRes = await schoolsApi.stats(params);
      if (sRes.data.data) {
        setStats({
          total: sRes.data.data.total || 0,
          is985: sRes.data.data.is985 || 0,
          is211: sRes.data.data.is211 || 0,
          dual: sRes.data.data.dual || 0,
          has_academician: sRes.data.data.has_academician || 0,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  // 省份变化时，加载对应的城市列表和更新统计
  const handleProvinceChange = async (province: string) => {
    const newFilters = { ...filters, province, city: '' }; // 清除城市选择
    setFilters(newFilters);
    await loadSchoolsWithFilters(newFilters, 1); // 重置到第一页

    // 加载该省份的城市
    if (province) {
      try {
        const cRes = await schoolsApi.cities(province);
        setCities(cRes.data.data || []);
      } catch (e) {
        console.error(e);
      }
    } else {
      // 恢复全部城市
      const cRes = await schoolsApi.cities();
      setCities(cRes.data.data || []);
    }

    // 更新统计数据（根据新选的省份）
    await loadStats(province, '');
  };

  // 分页变化
  const handlePageChange = async (newPage: number) => {
    await loadSchoolsWithFilters(filters, newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 搜索变化时重置到第一页
  const handleSearchChange = async (value: string) => {
    const newFilters = { ...filters, search: value };
    setFilters(newFilters);
    await loadSchoolsWithFilters(newFilters, 1);
  };

  // 过滤和排序
  const filteredSchools = useMemo(() => {
    let result = [...schools];
    
    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(term));
    }
    
    if (sortConfig) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key as keyof School];
        const bVal = b[sortConfig.key as keyof School];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [schools, filters.search, sortConfig]);

  const handleSort = (key: keyof School) => {
    setSortConfig(current => {
      if (current?.key === key) {
        if (current.direction === 'asc') return { key, direction: 'desc' };
        return null;
      }
      return { key, direction: 'asc' };
    });
  };

  const loadSchoolDetail = async (school: School) => {
    setSelectedSchool(school);
    setDetailLoading(true);
    try {
      // 根据当前学生的科目类型过滤录取数据
      const courseSelection = currentStudent?.subject_type || '物理类';
      const res = await schoolsApi.admission(school.name, courseSelection);
      setAdmissionData(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadAcademicians = async (schoolName: string) => {
    setAcademiciansLoading(true);
    try {
      const res = await schoolsApi.academicians(schoolName);
      setAcademicians(res.data.data || []);
    } catch (e) {
      console.error('加载院士数据失败:', e);
    } finally {
      setAcademiciansLoading(false);
    }
  };

  // 从卡片院士标识直接打开院士详情
  const openAcademicianView = async (school: School) => {
    setSelectedSchool(school);
    setDetailView('academician');
    setDetailLoading(true);
    try {
      // 根据当前学生的科目类型过滤录取数据
      const courseSelection = currentStudent?.subject_type || '物理类';
      const [admRes] = await Promise.all([
        schoolsApi.admission(school.name, courseSelection),
        loadAcademicians(school.name),
      ]);
      setAdmissionData(admRes.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  // 注意：stats 现在从后端 API 获取真实统计数据，不再基于当前页数据计算

  // 图表数据
  const chartData = useMemo(() => {
    const years = [2025, 2024, 2023, 2022].map(year => {
      const data = admissionData.find(d => d.year === year);
      return {
        year: year.toString(),
        最低分: data?.lowest_score,
        最低位次: data?.lowest_rank,
      };
    }).reverse();
    return years;
  }, [admissionData]);

  // 按年份降序排列的录取数据
  const sortedAdmissionData = useMemo(() => {
    return [...admissionData].sort((a, b) => b.year - a.year);
  }, [admissionData]);

  return (
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">院校探索</h1>
        <p className="text-gray-500 text-sm mt-1">
          浏览985/211高校在福建省历年录取数据
        </p>
      </div>

      {/* 移动端筛选按钮 */}
      <div className="md:hidden mb-3">
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 shadow-sm"
        >
          <SlidersHorizontal size={16} />
          筛选
          {(filters.type !== 'all' || filters.province || filters.city || filters.hasAcademician) && (
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          )}
          <ChevronDown size={14} className={`transition-transform ${filtersExpanded ? 'rotate-180' : ''}`} />
        </button>
        {filtersExpanded && (
          <div className="mt-2 space-y-3 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            {/* 类型快捷筛选 */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: '全部' },
                { key: '985', label: '985' },
                { key: '211', label: '211' },
                { key: 'dual', label: '双一流' },
              ].map(item => (
                <button
                  key={item.key}
                  onClick={() => {
                    const newFilters = { ...filters, type: item.key as any };
                    setFilters(newFilters);
                    loadSchoolsWithFilters(newFilters, 1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm ${filters.type === item.key ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600'}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  const newFilters = { ...filters, hasAcademician: !filters.hasAcademician };
                  setFilters(newFilters);
                  loadSchoolsWithFilters(newFilters, 1);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm ${filters.hasAcademician ? 'bg-amber-100 text-amber-700 font-medium' : 'bg-gray-100 text-gray-600'}`}
              >
                🏅 院士
              </button>
            </div>
            {/* 省份城市 */}
            <div className="flex gap-2">
              <select
                value={filters.province}
                onChange={e => handleProvinceChange(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl"
              >
                <option value="">全部省份</option>
                {provinces.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                value={filters.city}
                onChange={e => {
                  const newCity = e.target.value;
                  const newFilters = { ...filters, city: newCity };
                  setFilters(newFilters);
                  loadSchoolsWithFilters(newFilters);
                  loadStats(filters.province, newCity);
                }}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl"
              >
                <option value="">全部城市</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* 左侧筛选栏 — 桌面端显示 */}
        <div className="w-64 flex-shrink-0 space-y-4 hidden md:block">
          {/* 类型筛选 */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">院校类型</h3>
            <div className="space-y-2">
              {[
                { key: 'all', label: '全部', count: stats.total },
                { key: '985', label: '985工程', count: stats.is985, color: 'red' },
                { key: '211', label: '211工程', count: stats.is211, color: 'blue' },
                { key: 'dual', label: '双一流', count: stats.dual, color: 'purple' },
              ].map(item => (
                <button
                  key={item.key}
                  onClick={() => { 
                    const newFilters = { ...filters, type: item.key as any };
                    setFilters(newFilters);
                    loadSchoolsWithFilters(newFilters, 1);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${
                    filters.type === item.key
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{item.label}</span>
                  <span className={`text-xs ${filters.type === item.key ? 'text-blue-600' : 'text-gray-400'}`}>
                    {item.count}
                  </span>
                </button>
              ))}
            </div>
            {/* 院士筛选（独立开关） */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <label className="flex items-center justify-between px-3 py-2 rounded-xl text-sm cursor-pointer hover:bg-gray-50 transition-colors">
                <span className={`flex items-center gap-1.5 ${filters.hasAcademician ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                  🏅 有院士
                </span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${filters.hasAcademician ? 'text-amber-600' : 'text-gray-400'}`}>
                    {stats.has_academician}
                  </span>
                  <input
                    type="checkbox"
                    checked={filters.hasAcademician}
                    onChange={e => {
                      const newFilters = { ...filters, hasAcademician: e.target.checked };
                      setFilters(newFilters);
                      loadSchoolsWithFilters(newFilters, 1);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500/20"
                  />
                </div>
              </label>
            </div>
          </div>

          {/* 省份筛选 */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">所在省份</h3>
            <select
              value={filters.province}
              onChange={e => handleProvinceChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部省份</option>
              {provinces.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* 城市筛选 */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">所在城市</h3>
            <select
              value={filters.city}
              onChange={e => {
                const newCity = e.target.value;
                const newFilters = { ...filters, city: newCity };
                setFilters(newFilters);
                // 立即用新值加载数据
                loadSchoolsWithFilters(newFilters);
                // 更新统计数据（根据当前省份和新选的城市）
                loadStats(filters.province, newCity);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部城市</option>
              {cities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 右侧院校列表 */}
        <div className="flex-1">
          {/* 搜索和排序 */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="搜索院校名称..."
                value={filters.search}
                onChange={e => handleSearchChange(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-xl hidden sm:block">
              共 <span className="font-medium text-gray-700">{total}</span> 所院校
              {filters.province && <span className="ml-1">（{filters.province}）</span>}
              {filters.type !== 'all' && <span className="ml-1">（{filters.type === '985' ? '985' : filters.type === '211' ? '211' : '双一流'}）</span>}
            </div>
          </div>

          {/* 院校卡片网格 */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center text-gray-400">
              <SchoolIcon size={48} className="opacity-30 mx-auto mb-4" />
              <p>没有找到符合条件的院校</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSchools.map(school => (
                <button
                  key={school.id}
                  onClick={() => loadSchoolDetail(school)}
                  className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all text-left group"
                >
                  <div className="flex items-start gap-3">
                    {/* 校徽 / 首字母头像 */}
                    {school.logo_url ? (
                      <img 
                        src={school.logo_url} 
                        alt={school.name}
                        className="w-12 h-12 rounded-xl object-contain border border-gray-200 bg-white flex-shrink-0 p-0.5"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" style={{ display: school.logo_url ? 'none' : 'flex' }}>
                      {school.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 truncate group-hover:text-blue-700">
                        {school.name}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-500">{school.city} · {school.province}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* 标签 */}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {school.is985 === 1 && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-lg font-medium">985</span>
                    )}
                    {school.is985 !== 1 && school.is211 === 1 && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-lg font-medium">211</span>
                    )}
                    {school.is_dual_class === 1 && (
                      <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-lg font-medium">双一流</span>
                    )}
                    {school.has_academician === 1 && (
                      <span
                        className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-lg font-medium cursor-pointer hover:bg-amber-200 transition-colors"
                        onClick={e => { e.stopPropagation(); openAcademicianView(school); }}
                      >
                        🏅 院士{school.academician_count ? ` ${school.academician_count}` : ''}
                      </span>
                    )}
                  </div>

                  {/* 最新录取数据 */}
                  {school.latest_admission && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{school.latest_admission.year}年</span>
                        <span className="font-medium text-gray-700">
                          {school.latest_admission.lowest_score}分 / {school.latest_admission.lowest_rank?.toLocaleString()}名
                        </span>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 院校详情弹窗（含院士详情切换视图） */}
      {selectedSchool && (() => {
        const schoolView = (
          <div className="bg-white rounded-none md:rounded-2xl h-full md:h-auto md:max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
                  <div className="flex items-center gap-3">
                    {selectedSchool.logo_url ? (
                      <img 
                        src={selectedSchool.logo_url} 
                        alt={selectedSchool.name}
                        className="w-12 h-12 rounded-xl object-contain border border-gray-200 bg-white p-0.5"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold text-lg" style={{ display: selectedSchool.logo_url ? 'none' : 'flex' }}>
                      {selectedSchool.name[0]}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg text-gray-800">{selectedSchool.name}</h3>
                        {selectedSchool.website && (
                          <a
                            href={selectedSchool.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                            onClick={e => e.stopPropagation()}
                          >
                            官网
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <MapPin size={12} className="text-gray-400" />
                        <span className="text-sm text-gray-500">{selectedSchool.city} · {selectedSchool.province}</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { setSelectedSchool(null); setDetailView('school'); }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-4 md:p-6">
                  {/* 标签 */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedSchool.is985 === 1 && (
                      <span className="text-sm bg-red-100 text-red-600 px-3 py-1 rounded-lg font-medium">985工程</span>
                    )}
                    {selectedSchool.is985 !== 1 && selectedSchool.is211 === 1 && (
                      <span className="text-sm bg-blue-100 text-blue-600 px-3 py-1 rounded-lg font-medium">211工程</span>
                    )}
                    {selectedSchool.is_dual_class === 1 && (
                      <span className="text-sm bg-purple-100 text-purple-600 px-3 py-1 rounded-lg font-medium">双一流</span>
                    )}
                    {selectedSchool.has_academician === 1 && (
                      <span
                        className="text-sm bg-amber-100 text-amber-700 px-3 py-1 rounded-lg font-medium cursor-pointer hover:bg-amber-200 transition-colors"
                        onClick={() => { setDetailView('academician'); loadAcademicians(selectedSchool.name); }}
                      >
                        🏅 院士{selectedSchool.academician_count ? ` ${selectedSchool.academician_count}人` : ''}
                      </span>
                    )}
                  </div>

                  {/* 历年录取数据表格 */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800">福建省历年录取数据</h4>
                      {currentStudent?.subject_type && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          currentStudent.subject_type.includes('物理')
                            ? 'bg-blue-100 text-blue-700'
                            : currentStudent.subject_type.includes('历史')
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {currentStudent.subject_type}
                        </span>
                      )}
                    </div>
                    {detailLoading ? (
                      <div className="animate-pulse space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="h-10 bg-gray-200 rounded"></div>
                        ))}
                      </div>
                    ) : admissionData.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">年份</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">批次</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">科类</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">最低分</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">最低位次</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">平均分</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {sortedAdmissionData.map((d, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-3 py-2.5 text-gray-800">{d.year}年</td>
                                <td className="px-3 py-2.5 text-gray-600">{d.batch_name || '-'}</td>
                                <td className="px-3 py-2.5">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    d.course_selection_name?.includes('物理') 
                                      ? 'bg-blue-100 text-blue-700' 
                                      : d.course_selection_name?.includes('历史')
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {d.course_selection_name || '-'}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5 text-right font-medium text-gray-800">{d.lowest_score}</td>
                                <td className="px-3 py-2.5 text-right text-gray-600">{d.lowest_rank?.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right text-gray-600">{d.avg_score || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <Award size={32} className="opacity-30 mx-auto mb-2" />
                        <p>暂无录取数据</p>
                      </div>
                    )}
                  </div>

                  {/* 趋势图 */}
                  {admissionData.length > 1 && (
                    <div>
                      <h4 className="font-medium text-gray-800 mb-3">录取趋势</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="score" domain={['auto', 'auto']} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v: number) => v || '-'} />
                          <Legend />
                          <Line yAxisId="score" type="monotone" dataKey="最低分" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
        );

        const academicianView = (
          <div className="bg-white rounded-none md:rounded-2xl h-full md:h-auto md:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
                {/* 头部 */}
                <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200 flex-shrink-0">
                  <div>
                    <h3 className="font-semibold text-gray-800">{selectedSchool.name}</h3>
                    <p className="text-sm text-amber-700">🏅 院士 {selectedSchool.academician_count || academicians.length} 人</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setDetailView('school')} className="p-1.5 hover:bg-amber-200/50 rounded-lg" title="返回院校详情">
                      <ArrowLeft size={20} className="text-gray-500" />
                    </button>
                    <button onClick={() => { setSelectedSchool(null); setDetailView('school'); }} className="p-1.5 hover:bg-amber-200/50 rounded-lg" title="关闭">
                      <X size={20} className="text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* 院士列表 */}
                <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                  <AcademicianDetailPanel academicians={academicians} loading={academiciansLoading} />
                </div>

                {/* 底部 */}
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
                  <button
                    onClick={() => setDetailView('school')}
                    className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    <ArrowLeft size={14} />
                    返回院校详情
                  </button>
                  <span className="text-xs text-gray-400">数据来源：中国工程院/中国科学院</span>
                </div>
              </div>
        );

        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-0 md:p-4" onClick={() => { setSelectedSchool(null); setDetailView('school'); }}>
            {isSafari ? (
              /* Safari：状态切换（避免 3D 翻转的镜像 bug） */
              <div
                className="w-full h-full md:h-auto md:max-w-2xl relative"
                onClick={e => e.stopPropagation()}
              >
                {detailView === 'school' ? schoolView : academicianView}
              </div>
            ) : (
              /* Chrome/Edge/Firefox：3D 翻转动画 */
              <div
                className="w-full h-full md:h-auto md:max-w-2xl"
                onClick={e => e.stopPropagation()}
                style={{ perspective: '1200px' }}
              >
                <div
                  className="relative w-full"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: detailView === 'academician' ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    transition: 'transform 0.6s',
                  }}
                >
                  {/* 正面 */}
                  <div style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', pointerEvents: detailView === 'school' ? 'auto' : 'none' }}>
                    {schoolView}
                  </div>
                  {/* 背面 */}
                  <div
                    className="absolute top-0 left-0 w-full"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      pointerEvents: detailView === 'academician' ? 'auto' : 'none',
                    }}
                  >
                    {academicianView}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
